import { useCallback, useEffect, useState } from "react";
import api, { errorMessage } from "../lib/api";
import {
  SLOT_LABELS, assignAllReports, blockedReason, canPromote, choiceLabel, heldSlots, isSlotDecided, pendingCount, setReportChoice, setSlotChoice, summaryLine,
  totalChoices, vacantCount,
} from "../lib/handover";
import CollaboratorPicker from "./CollaboratorPicker";
import { Button } from "./ui/button";
import { AlertCircle, ArrowUp, Check, Scale } from "lucide-react";

// Ce que la personne a encore sous sa responsabilité (subordonnés ouverts, services actifs), lu avant d'enregistrer quoi que ce soit.
export function useHandoverImpact(open, collaboratorId) {
  const [impact, setImpact] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | error
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setState("loading");
    try {
      const { data } = await api.get(`/collaborators/${collaboratorId}/handover-impact`);
      setImpact(data);
      setState("ready");
    } catch (e) {
      setError(errorMessage(e));
      setState("error");
    }
  }, [collaboratorId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  return { impact, setImpact, state, error, reload: load };
}

// Un choix de responsable pour une personne : quelqu'un de l'annuaire (ou un compte CRM sans fiche), ou « Laisser vacant » — jamais un oubli.
function PersonChoice({ testid, choice, leaverId, onChange, vacantLabel = "Laisser vacant", extra }) {
  const vacant = choice?.kind === "vacant";
  return (
    <div className="flex flex-wrap gap-2 items-center lg:justify-end">
      {extra}
      <div className="w-full sm:w-56">
        <CollaboratorPicker testid={testid} excludeId={leaverId} allowAccounts placeholder="Choisir une personne"
          value={choice && choice.kind !== "vacant" && choice.kind !== "promote" ? choice.id : null} label={choice?.label}
          onChange={(id, name, kind) => onChange(id ? { kind: kind === "account" ? "account" : "collaborator", id, label: name } : null)} />
      </div>
      <Button type="button" variant={vacant ? "default" : "outline"} aria-pressed={vacant} data-testid={`${testid}-vacant`} className="h-10 sm:shrink-0"
        onClick={() => onChange(vacant ? null : { kind: "vacant" })}>
        {vacant && <Check size={14} className="mr-1.5" />}{vacantLabel}
      </Button>
    </div>
  );
}

function Decided({ done }) {
  return done
    ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><Check size={13} /> Choisi</span>
    : <span className="text-xs text-amber-700">À choisir</span>;
}

// `mode="exit"` : tout doit être décidé. `mode="reassign"` : n'importe quel sous-ensemble (la personne reste en poste).
export default function HandoverPanel({ impact, choices, onChange, leaver, leaverId, mode = "exit" }) {
  const blocked = blockedReason(impact);
  const total = totalChoices(impact);
  const vacant = vacantCount(impact, choices);
  const manager = impact.manager;
  const toManager = manager ? { kind: manager.kind === "account" ? "account" : "collaborator", id: manager.id || manager.user_id, label: manager.display_name } : null;
  const [bulk, setBulk] = useState(null);

  return (
    <section className="space-y-4" data-testid="handover-panel" aria-label="Passation des responsabilités">
      <div>
        <div className="flex items-center justify-between gap-2">
          {mode === "exit" ? <h3 className="text-base font-semibold text-slate-900">Passation avant le départ</h3> : <span className="text-sm text-slate-600">Seuls les choix faits sont enregistrés.</span>}
          <span className="text-xs text-slate-500" data-testid="handover-progress" aria-live="polite">{mode === "exit" ? summaryLine(impact, choices) : `${total - pendingCount(impact, choices)} choix fait${total - pendingCount(impact, choices) > 1 ? "s" : ""}`}</span>
        </div>
        {mode === "exit" && (
          <p className="text-sm text-slate-600 mt-1">
            {`${leaver} est encore responsable de ${impact.reports.length + impact.others_hidden} personne${impact.reports.length + impact.others_hidden > 1 ? "s" : ""} et de ${impact.services.length} service${impact.services.length > 1 ? "s" : ""}. Choisissez qui prend le relais : rien ne sera enregistré tant que tout n'est pas décidé.`}
          </p>
        )}
      </div>

      {impact.mandate_in_force && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 flex gap-2" role="note" data-testid="handover-mandate-notice">
          <Scale size={16} className="mt-0.5 shrink-0" />
          <span>Cette personne exerce un <strong>mandat légal</strong> en cours. Cette passation est opérationnelle : elle ne termine, ne transfère et ne modifie en rien le mandat. L'acte de cessation et les formalités restent à faire séparément.</span>
        </div>
      )}

      {blocked && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800 flex gap-2" role="alert" data-testid="handover-blocked">
          <AlertCircle size={16} className="mt-0.5 shrink-0" /> <span>{blocked}</span>
        </div>
      )}

      {impact.reports.length > 0 && (
        <div className="space-y-2">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
            <h4 className="text-sm font-medium text-slate-800">Personnes à rattacher ({impact.reports.length})</h4>
            <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
              {toManager && (
                <Button type="button" variant="outline" size="sm" className="h-10 sm:h-9 justify-start" data-testid="handover-up"
                  onClick={() => onChange(assignAllReports(impact, choices, toManager, true))}>
                  <ArrowUp size={14} className="mr-1.5" /> Tout remonter chez {manager.display_name}
                </Button>
              )}
              <div className="sm:w-60">
                <CollaboratorPicker testid="handover-all" excludeId={leaverId} allowAccounts placeholder="Tout confier à…" value={bulk?.id} label={bulk?.label}
                  onChange={(id, name, kind) => {
                    if (!id) { setBulk(null); return; }
                    const choice = { kind: kind === "account" ? "account" : "collaborator", id, label: name };
                    setBulk(choice);
                    onChange(assignAllReports(impact, choices, choice, true));
                  }} />
              </div>
            </div>
          </div>
          <ul className="divide-y divide-slate-200 rounded-md border border-slate-200">
            {impact.reports.map((report) => {
              const choice = choices.reports[report.id];
              return (
                <li key={report.id} className="p-3 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2" data-testid={`handover-report-${report.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{report.display_name}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-2">{report.job_title || "Collaborateur"} <Decided done={!!choice} /></div>
                  </div>
                  <PersonChoice testid={`handover-pick-${report.id}`} choice={choice} leaverId={leaverId} vacantLabel="Laisser sans responsable"
                    onChange={(next) => onChange(setReportChoice(choices, report.id, next))} />
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {impact.services.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-slate-800">Services à confier ({impact.services.length})</h4>
          <ul className="space-y-2">
            {impact.services.map((service) => (
              <li key={service.id} className="rounded-md border border-slate-200 p-3 space-y-3" data-testid={`handover-service-${service.id}`}>
                <div className="text-sm font-medium text-slate-900">{service.name}</div>
                {heldSlots(service).map((slot) => {
                  const choice = (choices.services[service.id] || {})[slot];
                  const promote = choice?.kind === "promote";
                  return (
                    <div key={slot} className="flex flex-col gap-2" data-testid={`handover-slot-${service.id}-${slot}`}>
                      <div className="text-xs text-slate-500 flex flex-wrap items-center gap-x-2 gap-y-1">
                        {SLOT_LABELS[slot]} actuel <Decided done={isSlotDecided(service, slot, choices)} />
                        {choice && <span className="text-slate-700">· {choiceLabel(choice, service)}</span>}
                      </div>
                      <PersonChoice testid={`handover-service-pick-${service.id}-${slot}`} choice={choice} leaverId={leaverId}
                        onChange={(next) => onChange(setSlotChoice(choices, service.id, slot, next))}
                        extra={slot === "head" && canPromote(service) && (
                          <Button type="button" variant={promote ? "default" : "outline"} aria-pressed={promote} className="h-10" data-testid={`handover-promote-${service.id}`}
                            onClick={() => onChange(setSlotChoice(choices, service.id, "head", promote ? null : { kind: "promote" }))}>
                            Promouvoir {service.deputy.display_name}
                          </Button>
                        )} />
                    </div>
                  );
                })}
              </li>
            ))}
          </ul>
        </div>
      )}

      {vacant > 0 && (
        <p className="text-xs text-slate-600" data-testid="handover-vacant-note">
          {vacant} poste{vacant > 1 ? "s" : ""} laissé{vacant > 1 ? "s" : ""} vacant{vacant > 1 ? "s" : ""} volontairement : le contrôle de cohérence les signalera jusqu'à ce que quelqu'un soit désigné.
        </p>
      )}
      {mode === "exit" && pendingCount(impact, choices) > 0 && total > 0 && (
        <p className="text-xs text-amber-700" data-testid="handover-pending">Il reste {pendingCount(impact, choices)} choix à faire avant de pouvoir enregistrer la sortie.</p>
      )}
    </section>
  );
}
